import React, { Component } from 'react';
import DatePicker from 'react-datepicker';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

import { getSelectedBranchId } from '../utils/branch';

var config = process.env;

// Panel de cafetería (ENI-249).
//
// El canal arrancó el 01/08, así que la pantalla responde "¿está funcionando?" y no "¿cómo lo
// optimizo?". Dos cosas se dicen en la cara y no en una nota al pie, porque son las que hacen que
// alguien saque una conclusión equivocada:
//
//  - la MERMA no se puede calcular hoy (el consumo lo genera la propia receta al vender, así que
//    el ledger no es una medición independiente; haría falta conteo físico de insumos);
//  - la mediana de entrega sale de las comandas marcadas de a una, porque las que se cierran en
//    lote al final de la noche miden cuándo alguien tocó el botón.

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 0 })}`);
const number = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE'));
const percent = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const minutos = (v) => (v == null ? '—' : `${Number(v)} min`);

const CATEGORIAS = {
    hot_drink: 'Bebidas calientes',
    cold_drink: 'Bebidas frías',
    bakery: 'Panadería',
    snack: 'Snacks',
    pizzeria: 'Pizzería',
    other: 'Otros',
};
const rotulo = (c) => CATEGORIAS[c] || c;

function toIsoDate(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

class CafeteriaAnalyticsComponent extends Component {
    constructor(props) {
        super(props);
        const hoy = new Date();
        const desde = new Date();
        // El canal arrancó el 01/08/2026: un default de 11 meses mostraría 10 meses vacíos.
        desde.setDate(desde.getDate() - 30);
        // `modo` alterna entre la foto agregada (lo de siempre) y el desglose por día. No es un
        // filtro: son dos preguntas distintas. "General" responde cómo viene el canal; "Por día"
        // responde con qué stockearse para el sábado, que es lo que se usa para pedir.
        this.state = { from: desde, to: hoy, loaded: false, error: null, data: null, modo: 'general' };
    }

    componentDidMount() { this.cargar(); }

    cargar = () => {
        this.setState({ loaded: false, error: null });
        const params = new URLSearchParams({ from: toIsoDate(this.state.from), to: toIsoDate(this.state.to) });
        const branchId = getSelectedBranchId();
        if (branchId) params.set('branchId', branchId);

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}analytics/cafeteria?${params.toString()}`, {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        })
            .then((r) => r.json())
            .then((body) => {
                if (body.error > 0) {
                    if (body.error === 1002) { window.location.href = localStorage.getItem('homePageURL'); return; }
                    this.setState({ loaded: true, error: body.message, data: null });
                    return;
                }
                this.setState({ loaded: true, data: body.data, error: null });
            })
            .catch((e) => this.setState({ loaded: true, error: e.message, data: null }));
    };

    // ── Modo "Por día" ────────────────────────────────────────────────────────────────────────
    //
    // Todo acá está expresado POR DÍA ABIERTO y no en totales. "Se vendieron 39 chipás los
    // sábados" no dice cuántos comprar; "5,8 chipás por sábado" sí. Y el número de días sobre
    // el que se promedió va SIEMPRE al lado: con un solo sábado medido, el promedio es una
    // anécdota y quien pide tiene que poder verlo antes de comprar de más.
    renderPorDia() {
        const { byWeekday, byDayType } = this.state.data;
        const maxVenta = Math.max(...byWeekday.map((d) => d.avgRevenue || 0), 1);
        const pocosDatos = (dias) => dias > 0 && dias < 3;

        return (
            <div>
                <div className="card card-small mb-4">
                    <div className="card-header border-bottom">
                        <h6 className="m-0">Qué día se vende</h6>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            Promedio <strong>por día abierto</strong>, no total: es lo comparable entre un sábado
                            que cayó dos veces en el rango y un miércoles que cayó una.
                        </span>
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-sm mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0">Día</th>
                                    <th className="border-0 text-right">Días abiertos</th>
                                    <th className="border-0 text-right">Ventas / día</th>
                                    <th className="border-0 text-right">Venta / día</th>
                                    <th className="border-0" style={{ width: '35%' }}>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byWeekday.length === 0 && (
                                    <tr><td colSpan="5" className="text-center text-muted py-3">Sin ventas en el período.</td></tr>
                                )}
                                {byWeekday.map((d) => (
                                    <tr key={d.weekday} className={d.isWeekend ? 'bg-light' : ''}>
                                        <td>
                                            {d.label}
                                            {d.isWeekend && <span className="badge badge-info ml-2" style={{ fontSize: 9 }}>finde</span>}
                                        </td>
                                        <td className="text-right">
                                            {number(d.openDays)}
                                            {/* Sin esta marca, un promedio de UN día se lee igual que uno de diez. */}
                                            {pocosDatos(d.openDays) && (
                                                <span className="text-warning ml-2" style={{ fontSize: 10 }}>pocos datos</span>
                                            )}
                                        </td>
                                        <td className="text-right">{d.avgOrders == null ? '—' : d.avgOrders.toFixed(1)}</td>
                                        <td className="text-right font-weight-bold">{money(d.avgRevenue)}</td>
                                        <td>
                                            <div style={{ background: '#e9ecef', borderRadius: 3, height: 14 }}>
                                                <div style={{
                                                    width: `${Math.round(((d.avgRevenue || 0) / maxVenta) * 100)}%`,
                                                    background: d.isWeekend ? '#17c671' : '#007bff',
                                                    height: '100%', borderRadius: 3,
                                                }} />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="card card-small mb-4">
                    <div className="card-header border-bottom">
                        <h6 className="m-0">Con qué stockearse</h6>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            Unidades <strong>por día abierto</strong> de cada tipo, sobre{' '}
                            {number(byDayType.weekdayOpenDays)} día(s) de semana y{' '}
                            {number(byDayType.weekendOpenDays)} de fin de semana. La última columna es cuántas
                            veces más se vende un día de finde.
                        </span>
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-sm mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0">Ítem</th>
                                    <th className="border-0">Categoría</th>
                                    <th className="border-0 text-right">Por día de semana</th>
                                    <th className="border-0 text-right">Por día de finde</th>
                                    <th className="border-0 text-right">Diferencia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byDayType.items.length === 0 && (
                                    <tr><td colSpan="5" className="text-center text-muted py-3">Sin ventas en el período.</td></tr>
                                )}
                                {byDayType.items.map((i) => (
                                    <tr key={i.code}>
                                        <td>{i.name}</td>
                                        <td className="text-muted">{rotulo(i.category)}</td>
                                        <td className="text-right">{i.weekdayPerDay == null ? '—' : i.weekdayPerDay.toFixed(1)}</td>
                                        <td className="text-right">{i.weekendPerDay == null ? '—' : i.weekendPerDay.toFixed(1)}</td>
                                        <td className="text-right">
                                            {/* null cuando entre semana es 0: ahí el cociente sería infinito.
                                                Se muestra "sólo finde", que es lo que realmente pasa. */}
                                            {i.weekendLift == null
                                                ? <span className="text-muted">{i.weekendUnits > 0 ? 'sólo finde' : '—'}</span>
                                                : <span className={i.weekendLift >= 1.5 ? 'text-success font-weight-bold' : ''}>
                                                    ×{i.weekendLift.toFixed(1)}
                                                </span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { loaded, error, data } = this.state;

        if (!loaded) return <div className="text-center text-muted py-5">Cargando…</div>;
        if (error) return <div className="alert alert-warning">{error}</div>;
        if (!data) return null;

        const { categories, items, hourly, kitchen, ledger, supplies, health } = data;
        const ventas = categories.reduce((s, c) => s + (c.revenue || 0), 0);
        const margen = categories.reduce((s, c) => s + (c.margin || 0), 0);
        const unidades = categories.reduce((s, c) => s + (c.units || 0), 0);
        const sinCosto = items.reduce((s, i) => s + (i.linesWithoutCost || 0), 0);
        // Sólo se agrupan las que tienen cobertura calculable; el resto no se consumió en el período.
        const porAgotarse = supplies.filter((s) => s.daysOfCover != null && s.daysOfCover <= 30);

        return (
            <div>
                <div className="card card-small mb-4">
                    <div className="card-body py-3">
                        <div className="d-flex flex-wrap align-items-end">
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Desde</label>
                                <DatePicker selected={this.state.from} onChange={(d) => this.setState({ from: d })} dateFormat="dd/MM/yyyy" className="form-control" />
                            </div>
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Hasta</label>
                                <DatePicker selected={this.state.to} onChange={(d) => this.setState({ to: d })} dateFormat="dd/MM/yyyy" className="form-control" />
                            </div>
                            <div className="mr-3 mb-2">
                                <button className="btn btn-primary" onClick={this.cargar}>Aplicar</button>
                            </div>
                            <div className="mb-2 ml-auto">
                                <label className="d-block" style={{ fontSize: 12 }}>Ver</label>
                                <div className="btn-group">
                                    {[['general', 'General'], ['dia', 'Por día']].map(([k, t]) => (
                                        <button
                                            key={k}
                                            className={`btn btn-sm ${this.state.modo === k ? 'btn-primary' : 'btn-white'}`}
                                            onClick={() => this.setState({ modo: k })}
                                        >{t}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Los cuatro números ── */}
                <div className="row">
                    {[
                        {
                            // "Ventas del canal" colisionaba con la fila `cafeteria` del panel de
                            // Ventas, que suma TICKETS: para las mismas 153 ventas una pantalla
                            // decía $47.221 y la otra $47.158. Acá se suman LÍNEAS de gastronomía,
                            // que es la unidad correcta para desglosar por categoría e ítem, pero
                            // no incluye cupones ni envío. El rótulo lo dice.
                            t: 'Venta de gastronomía', v: money(ventas),
                            s: `${number(unidades)} unidades · suma de líneas`,
                        },
                        {
                            t: 'Margen', v: money(margen),
                            s: ventas > 0 ? `${percent((margen / ventas) * 100)} sobre la venta` : '—',
                        },
                        {
                            t: 'Entrega (mediana)', v: minutos(kitchen.medianMinutes),
                            // El denominador va en el subtítulo: con 3 comandas sueltas la mediana
                            // es una anécdota, y eso tiene que verse sin abrir el detalle.
                            s: `${number(kitchen.individualCount)} de ${number(kitchen.measurable)} entregas medidas de a una`,
                        },
                        { t: 'Ítems del menú', v: number(health.menuItems), s: `${number(health.withIncompleteCost)} con costo incompleto` },
                    ].map((k) => (
                        <div className="col-lg-3 col-md-6 col-sm-6 mb-4" key={k.t}>
                            <div className="card card-small h-100">
                                <div className="card-body p-3">
                                    <span className="text-muted d-block" style={{ fontSize: 12 }}>{k.t}</span>
                                    <h5 className="my-1">{k.v}</h5>
                                    <span className="text-muted d-block" style={{ fontSize: 11 }}>{k.s}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Avisos: lo que estos números NO dicen ── */}
                {(kitchen.batchMarked > 0 || sinCosto > 0 || ledger.salesWithoutMovement > 0) && (
                    <div className="alert alert-light border mb-4" style={{ fontSize: 13 }}>
                        <strong className="d-block mb-1">Cómo leer estos números</strong>
                        {kitchen.batchMarked > 0 && (
                            <div>
                                • {number(kitchen.batchMarked)} de {number(kitchen.measurable)} entregas se marcaron
                                en lote (tres o más en el mismo minuto), así que miden cuándo se tocó el botón y no
                                cuándo salió el pedido. Están excluidas de la mediana; con todas adentro daría{' '}
                                {minutos(kitchen.medianMinutesAll)}.
                            </div>
                        )}
                        {kitchen.withReadyStep < kitchen.delivered && (
                            <div>
                                • El paso "listo" se marca en {number(kitchen.withReadyStep)} de {number(kitchen.delivered)}{' '}
                                comandas: no se puede separar el tiempo de cocina del de entrega.
                            </div>
                        )}
                        {sinCosto > 0 && (
                            <div>• {number(sinCosto)} líneas vendidas sin costo cargado: su margen no se cuenta (no se estima en cero).</div>
                        )}
                        {ledger.salesWithoutMovement > 0 && (
                            <div className="text-danger">
                                • {number(ledger.salesWithoutMovement)} de {number(ledger.salesWithRecipe)} ventas con receta
                                no descontaron insumos. Ese stock quedó fantasma.
                            </div>
                        )}
                    </div>
                )}

                {this.state.modo === 'dia' && this.renderPorDia()}

                {this.state.modo === 'general' && (
                <React.Fragment>
                <div className="row">
                    {/* ── Categorías ── */}
                    <div className="col-lg-6 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom"><h6 className="m-0">Por categoría</h6></div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0">Categoría</th>
                                            <th className="border-0 text-right">Unidades</th>
                                            <th className="border-0 text-right">Venta</th>
                                            <th className="border-0 text-right">Margen</th>
                                            <th className="border-0 text-right">%</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.length === 0 && (
                                            <tr><td colSpan="5" className="text-center text-muted py-3">Sin ventas en el período.</td></tr>
                                        )}
                                        {categories.map((c) => (
                                            <tr key={c.category}>
                                                <td>{rotulo(c.category)}</td>
                                                <td className="text-right">{number(c.units)}</td>
                                                <td className="text-right">{money(c.revenue)}</td>
                                                <td className="text-right">{money(c.margin)}</td>
                                                <td className="text-right">{percent(c.marginPct)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── Curva horaria ── */}
                    <div className="col-lg-6 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">A qué hora se consume</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>Pedidos de cafetería y mesas, por hora del día.</span>
                            </div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={hourly.map((h) => ({ ...h, label: `${h.hour}h` }))}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip formatter={(v, n) => (n === 'revenue' ? money(v) : number(v))} />
                                        {/* Sin animación: si el componente re-renderiza mientras
                                            corre el tween, recharts deja las barras congeladas a
                                            media altura. Se ven proporcionales entre sí, así que
                                            el gráfico parece correcto y sólo está mal la escala. */}
                                        <Bar dataKey="orders" name="Pedidos" fill="#007bff" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Ranking de ítems ── */}
                <div className="card card-small mb-4">
                    <div className="card-header border-bottom">
                        <h6 className="m-0">Ítems del menú</h6>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            El costo es el que quedó guardado en cada venta (receta × insumos al momento de venderla).
                        </span>
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-sm mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0">Ítem</th>
                                    <th className="border-0">Categoría</th>
                                    <th className="border-0 text-right">Unidades</th>
                                    <th className="border-0 text-right">Venta</th>
                                    <th className="border-0 text-right">Margen</th>
                                    <th className="border-0 text-right">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 && (
                                    <tr><td colSpan="6" className="text-center text-muted py-3">Sin ventas en el período.</td></tr>
                                )}
                                {items.map((i) => (
                                    <tr key={i.code}>
                                        <td>
                                            {i.name}
                                            {i.linesWithoutCost > 0 && (
                                                <span className="badge badge-warning ml-2" style={{ fontSize: 10 }}>
                                                    {i.linesWithoutCost} sin costo
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-muted">{rotulo(i.category)}</td>
                                        <td className="text-right">{number(i.units)}</td>
                                        <td className="text-right">{money(i.revenue)}</td>
                                        <td className="text-right">{money(i.margin)}</td>
                                        <td className="text-right">{percent(i.marginPct)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="row">
                    {/* ── Insumos por agotarse ── */}
                    <div className="col-lg-6 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Insumos por agotarse</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Días de stock al ritmo de los días que tuvieron consumo. Sin consumo no hay ritmo, y esos no aparecen.
                                </span>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0">Insumo</th>
                                            <th className="border-0 text-right">Disponible</th>
                                            <th className="border-0 text-right">Días</th>
                                            <th className="border-0 text-right">Medido sobre</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {porAgotarse.length === 0 && (
                                            <tr><td colSpan="4" className="text-center text-muted py-3">Ningún insumo por debajo de 30 días.</td></tr>
                                        )}
                                        {porAgotarse.map((s) => (
                                            <tr key={s.code}>
                                                <td>{s.name}</td>
                                                <td className="text-right">{number(s.available)} {s.unit}</td>
                                                <td className={`text-right ${s.daysOfCover <= 7 ? 'text-danger font-weight-bold' : ''}`}>
                                                    {number(s.daysOfCover)}
                                                </td>
                                                {/* El denominador a la vista: con 2 días de consumo, un "16d" es ruido. */}
                                                <td className="text-right text-muted" style={{ fontSize: 12 }}>
                                                    {number(s.activeDays)} {s.activeDays === 1 ? 'día' : 'días'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── Consumo de insumos ── */}
                    <div className="col-lg-6 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Consumo de insumos</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Es lo que las recetas descontaron al vender, no una medición física.
                                </span>
                            </div>
                            <div className="card-body p-0">
                                {/* Esto no es una nota al pie: sin decirlo, la tabla de arriba se lee
                                    como si fuera control de merma, que es justo lo que NO es. */}
                                <div className="px-3 py-2 border-bottom bg-light" style={{ fontSize: 12 }}>
                                    {ledger.supplyPhysicalCounts === 0
                                        ? 'La merma (desperdicio, rotura, faltantes) no se puede calcular todavía: haría falta conteo físico de insumos, y no hay ninguno registrado.'
                                        : `Hay ${number(ledger.supplyPhysicalCounts)} conteos físicos de insumos: ya se puede empezar a contrastar el consumo teórico contra el real.`}
                                </div>
                                <table className="table table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0">Insumo</th>
                                            <th className="border-0 text-right">Consumido</th>
                                            <th className="border-0 text-right">Valorizado</th>
                                            <th className="border-0 text-right">Ajustes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ledger.items.length === 0 && (
                                            <tr><td colSpan="4" className="text-center text-muted py-3">Sin movimientos en el período.</td></tr>
                                        )}
                                        {ledger.items.slice(0, 12).map((i) => (
                                            <tr key={i.code}>
                                                <td>{i.name}</td>
                                                <td className="text-right">{number(i.consumed)} {i.unit}</td>
                                                <td className="text-right">{money(i.consumedValued)}</td>
                                                <td className="text-right">{i.adjustments > 0 ? number(i.adjustments) : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
                </React.Fragment>
                )}
            </div>
        );
    }
}

export default CafeteriaAnalyticsComponent;
